import { getCannedValueClosedReason, getIsCannedValueClosed } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export function getIsCannedFieldValueLocked(
  effectiveValue: string | null,
  isFocused: boolean
): boolean {
  return effectiveValue != null && !isFocused;
}

/**
 * `isValueLocked` alone does not survive the end of signing:
 * `computeCannedFieldDefault` returns null for a Title or Address the signer
 * never filled in, so those rows can stay empty after finish. Gate on the
 * assignee's status directly.
 */
export function getCanEditCannedFieldRow(args: {
  assigneeStatus: Apollo.WorkflowRecipientStatus | null | undefined;
  isAssignedToCurrentUser: boolean;
  isAutoOnly: boolean;
  isValueLocked: boolean;
  wasSentForSignature: boolean;
}): boolean {
  return (
    args.wasSentForSignature &&
    args.isAssignedToCurrentUser &&
    !args.isAutoOnly &&
    !getIsCannedValueClosed(args.assigneeStatus) &&
    !args.isValueLocked
  );
}

export function getCannedFieldRowDisabledReason(args: {
  assigneeStatus: Apollo.WorkflowRecipientStatus | null | undefined;
  canEdit: boolean;
  isAssignedToCurrentUser: boolean;
  isAutoOnly: boolean;
  isValueLocked: boolean;
  variant: Apollo.CannedFieldVariant;
  wasSentForSignature: boolean;
}): string {
  if (args.canEdit) return '';

  const closedReason = getCannedValueClosedReason(args.assigneeStatus);
  if (closedReason) return closedReason;
  if (args.isAutoOnly) {
    if (args.variant === Apollo.CannedFieldVariant.DateSigned)
      return 'Auto-filled with the signing date';
    return "Auto-filled with the signer's email";
  }
  if (!args.wasSentForSignature) return 'Editable during signing only';
  if (!args.isAssignedToCurrentUser) return 'Assigned to another signer';
  if (args.isValueLocked) return 'Open the Edit drawer to change this value';
  return '';
}

export function getCannedFieldRowDisplayValue(args: {
  canEdit: boolean;
  defaultValue: string | null;
  isAutoOnly: boolean;
  savedValue: string | null;
  typedValue: string;
}): string {
  if (args.isAutoOnly) return args.savedValue ?? args.defaultValue ?? '';
  if (args.canEdit) return args.typedValue;
  return args.savedValue ?? args.defaultValue ?? '';
}
